<template>
  <div
    class="d-flex position-relative"
    style="height: 100%;"
  >
    <ChartValues
      v-for="(marginTop, id) in zoomBtnMarginTop"
      :key="id"
      :style="{marginTop, display: (chartRatios[id] > 0 ? 'block' : 'none')}"
      :type="chartNames[id]"
      :visible="chartRatios[id] > 0"
      :data="currentDataPoint"
      :prev-data="prevDataPoint"
    />

    <div v-if="!isZoomIn">
      <div
        v-for="(marginTop, id) in moveBtnMarginTop"
        :key="id"
        :style="{marginTop}"
        class="d-flex flex-column justify-space-around container-move"
      >
        <v-btn
          v-if="id > 0"
          small
          icon
          @click="move(id, true)"
        >
          <v-icon>mdi-chevron-up</v-icon>
        </v-btn>
        <v-btn
          v-if="id < moveBtnMarginTop.length - 1"
          small
          icon
          @click="move(id, false)"
        >
          <v-icon>mdi-chevron-down</v-icon>
        </v-btn>
      </div>
    </div>

    <div v-if="!isZoomIn && chartNames.length > 1">
      <v-btn
        v-for="(marginTop, id) in zoomBtnMarginTop"
        :key="id"
        :style="{marginTop}"
        class="btn-zoom"
        small
        icon
        @click="zoomIn(id)"
      >
        <v-icon>mdi-arrow-expand</v-icon>
      </v-btn>
    </div>

    <!-- Zoom Out -->
    <v-btn
      v-if="isZoomIn && chartNames.length > 1"
      class="btn-zoom"
      small
      icon
      @click="zoomOut"
    >
      <v-icon>mdi-arrow-collapse</v-icon>
    </v-btn>

    <ChartSlider
      ref="slider"
      v-model="sliderValue"
      :step="5"
      :disabled="isZoomIn"
    ></ChartSlider>
    <div
      ref="chartContainer"
      class="lw-chart"
    ></div>
  </div>
</template>

<script>
import { createChart } from 'lightweight-charts'

import {
  computed, getCurrentInstance, ref, reactive, watch,
} from '@vue/composition-api'
import ChartValues from './ChartValues.vue'
import ChartSlider from './ChartSlider.vue'
import { evaluate } from '../dsl/evaluator'
import { moneyFlowProgram, adwinProgram } from '../dsl/programs'
import {
  baseChartOptions, candleSeriesOptions, priceLine, timezoneOffset,
} from '../utils/chartConstants'
import useChartData from '../composables/useChartData'
import useChartLayout from '../composables/useChartLayout'
import useChartSync from '../composables/useChartSync'
import useChartZoom from '../composables/useChartZoom'
import useChartMove from '../composables/useChartMove'
import { applyTick } from '../utils/rawData'

const timeIndexes = {}

// The program's values, or null when it throws: its `ema` throws on fewer bars than its period
// (80 for money flow, so a recent listing or a first weekly page). Its panes are then left
// empty and the others drawn.
function runProgram(program, {
  o = [], h = [], l = [], c = [], v = [],
}) {
  try {
    return evaluate(program, {
      open: o, high: h, low: l, close: c, volume: v,
    })
  } catch (e) {
    return null
  }
}

// A point with a value, or only its time (a gap) when there is none
const point = (time, value) => (Number.isFinite(value) ? { time, value } : { time })

const lineWidth = 1

const watermark = document.createElement('div')
watermark.style.zIndex = '2'
watermark.style.height = '80px'
watermark.style.width = '390px'
watermark.style.position = 'absolute'
watermark.style.bottom = '50px'
watermark.style.right = '50px'
watermark.style.opacity = '0.2'
watermark.style.backgroundRepeat = 'no-repeat'
watermark.style.backgroundPosition = 'center'
watermark.style.backgroundSize = 'contain'
watermark.style.backgroundImage = `url(${require('@/assets/images/logos/logo.png')})`

const nbfSeriesOptions = {
  lineWidth,
  color: 'green',
  priceLineVisible: false,
}

const nsfSeriesOptions = {
  lineWidth,
  color: 'red',
  priceLineVisible: false,
}

const diffSeriesOptions = {
  lineWidth,
  priceLineVisible: false,
  topLineColor: 'green',
  topFillColor1: 'rgba(0,128,0.028)',
  topFillColor2: 'white',
  bottomLineColor: 'red',
  bottomFillColor1: 'white',
  bottomFillColor2: 'rgba(255,0,0.028)',
}

const volumeSeriesOptions = {
  upColor: 'green',
  downColor: 'red',
  priceFormat: {
    type: 'volume',
  },
  priceLineVisible: false,
}

const adwinSeriesOptions = {
  color: 'brown',
  lineWidth: 1,
  priceLineVisible: false,
}

export default {
  components: {
    ChartValues,
    ChartSlider,
  },
  props: {
    symbol: {
      type: String,
      default: '',
    },
    resolution: {
      type: String,
      default: '1D',
    },
    chartConfig: {
      type: Array,
      default: () => ['candle', 'volume'],
    },
  },
  setup(props, { emit }) {
    const ins = getCurrentInstance()

    // Create reactive chart configuration based on props
    const CHART_NAMES = computed(() => props.chartConfig)

    const initialNames = [...CHART_NAMES.value]
    const charts = reactive(initialNames.map(() => null))
    const chartNames = reactive([...initialNames])

    const series = reactive({})
    const seriesMap = reactive({})

    // Initialize series and seriesMap based on chart configuration
    const initializeSeries = () => {
      // Clear existing series
      Object.keys(series).forEach(key => delete series[key])
      Object.keys(seriesMap).forEach(key => delete seriesMap[key])

      CHART_NAMES.value.forEach(name => {
        if (name === 'money') {
          series.nbf = null
          series.nsf = null
          seriesMap[name] = 'nbf'
        } else {
          series[name] = null
          seriesMap[name] = name
        }
      })
    }

    initializeSeries()

    // Watch for changes to chartConfig and reinitialize
    watch(() => props.chartConfig, newNames => {
      chartNames.splice(0, chartNames.length, ...newNames)
      charts.splice(0, charts.length, ...newNames.map(() => null))
      initializeSeries()
    })

    const getDataPoint = (currentData, index) => {
      const dataPoint = {}
      CHART_NAMES.value.forEach(name => {
        if (name === 'money') {
          dataPoint.money = {
            nbf: currentData.nbf?.[index],
            nsf: currentData.nsf?.[index],
          }
        } else {
          dataPoint[name] = currentData[name]?.[index]
        }
      })

      return dataPoint
    }

    const chartData = useChartData({ getDataPoint })

    const nCharts = computed(() => CHART_NAMES.value.length)
    const nSliders = computed(() => nCharts.value - 1)

    // Initialize slider with evenly distributed breakpoints
    const sliderValue = ref(
      Array.from({ length: nSliders.value }, (__, idx) => Math.round((100 * (idx + 1)) / nCharts.value)),
    )

    // Watch for changes in number of charts and reinitialize slider
    watch(nSliders, newN => {
      if (newN === 0) {
        sliderValue.value = []
      } else {
        sliderValue.value = Array.from({ length: newN }, (__, idx) => Math.round((100 * (idx + 1)) / (newN + 1)))
      }
    })

    // chartRatios is also used in the template for panel visibility
    const chartRatios = computed(() => {
      const ratios = []

      // Handle single chart case (no sliders)
      if (nSliders.value === 0) {
        return [100]
      }

      // First chart: from top (100) down to last slider
      ratios.push(Math.max(0, 100 - sliderValue.value[nSliders.value - 1]))
      // Middle charts: between consecutive sliders (bottom-up)
      for (let i = nSliders.value - 1; i > 0; i -= 1) {
        ratios.push(Math.max(0, sliderValue.value[i] - sliderValue.value[i - 1]))
      }
      // Last chart: from first slider down to bottom (0)
      ratios.push(Math.max(0, sliderValue.value[0]))

      return ratios
    })
    const panelRatios = computed(() => chartRatios.value.map(r => r / 100))

    const zoom = useChartZoom({
      charts,
      sliderValue,
      zoomedSliderValueFor: chartId => {
        // Collapse all charts except the selected one
        // Charts are ordered top-to-bottom: [candle, money, diff, volume, ...]
        // Sliders are ordered bottom-to-top: [bottom_boundary, middle_boundary, top_boundary]
        // To zoom chart N, collapse charts before N (set top sliders to 100)
        const result = Array(nSliders.value).fill(0)
        // Set sliders from (nSliders - chartId) to end to 100
        // This collapses all charts above the selected one
        for (let i = nSliders.value - 1; i >= nSliders.value - chartId; i -= 1) {
          result[i] = 100
        }

        return result
      },
    })

    const layout = useChartLayout({
      getChartContainer: () => ins.proxy.$refs.chartContainer,
      getSlider: () => ins.proxy.$refs.slider,
      charts,
      panelRatios,
    })

    const sync = useChartSync({
      charts,
      getSeriesByPanelId: id => series[seriesMap[chartNames[id]]],
      timeIndexes,
      currentTimeIndex: chartData.currentTimeIndex,
      emit,
      options: { initTimeIndexOnRangeChange: true },
    })

    const moveComposable = useChartMove({
      charts,
      swapPanelNames: (id, swapId) => {
        [chartNames[id], chartNames[swapId]] = [chartNames[swapId], chartNames[id]]
      },
      initCharts: () => ins.proxy.initCharts(),
      setChartsHeight: layout.setChartsHeight,
      setChartsWidth: layout.setChartsWidth,
      replayData: () => {
        Object.keys(series).forEach(key => {
          if (series[key] && ins.proxy.currentData[key]) {
            series[key].setData(ins.proxy.currentData[key])
          }
        })
      },
    })

    return {
      sliderValue,
      chartRatios,
      chartNames,
      charts,
      series,
      seriesMap,
      CHART_NAMES,
      ...chartData,
      ...zoom,
      ...layout,
      ...sync,
      ...moveComposable,
    }
  },
  watch: {
    symbol() {
      this.resetZoom()
    },
    resolution(value) {
      this.resetZoom()
      const timeVisible = !['1D', '1W'].includes(value)
      this.charts.forEach(chart => chart && chart.applyOptions({
        timeScale: {
          timeVisible,
        },
      }))
    },
    rawData(data, oldData) {
      const seriesData = this.computeData(data)
      Object.keys(this.series).forEach(key => {
        if (this.series[key] && seriesData[key]) {
          this.series[key].setData(seriesData[key])
        }
      })

      // First load
      if (!oldData.t) this.resetZoom()
    },
  },

  mounted() {
    this.initCharts()
    window.addEventListener('keydown', this.onKey)
  },

  // The chart is mounted anew each time its panes change, so what it holds must go with it
  beforeDestroy() {
    window.removeEventListener('keydown', this.onKey)
    this.charts.forEach(chart => chart && chart.remove())
  },

  methods: {
    // Left and right step the crosshair one bar, unless the key is a field's
    onKey(event) {
      const { key } = event
      if (key !== 'ArrowLeft' && key !== 'ArrowRight') return
      if (event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return
      if (event.target.closest?.('input, textarea, select, [contenteditable]')) return
      if (!this.currentTimeIndex) return

      const newIndex = this.currentTimeIndex + (key === 'ArrowLeft' ? -1 : 1)
      const candleData = this.currentData.candle || this.currentData[Object.keys(this.currentData)[0]]
      const newTime = candleData?.[newIndex]?.time || 0
      if (!newTime) return

      this.charts.forEach((chart, id) => {
        chart.setCrosshairPosition(0, newTime, this.series[this.seriesMap[this.chartNames[id]]])
      })

      this.currentTimeIndex = newIndex
    },
    initCharts() {
      const container = this.$refs.chartContainer
      container.appendChild(watermark)
      const len = this.charts.length
      const lastIdx = len - 1
      // eslint-disable-next-line no-plusplus
      for (let i = 0; i < len; i++) {
        const options = {
          ...baseChartOptions,
          timeScale: { ...baseChartOptions.timeScale, visible: i === lastIdx },
          // A vertical swipe scrolls the page: on a phone the charts take most of the screen
          handleScroll: { vertTouchDrag: false },
        }
        this.charts[i] = createChart(container, options)
      }

      this.charts.forEach((chart, idx) => {
        switch (this.chartNames[idx]) {
          case 'candle':
            this.series.candle = chart.addCandlestickSeries(candleSeriesOptions)
            break
          case 'money':
            this.series.nbf = chart.addLineSeries(nbfSeriesOptions)
            this.series.nsf = chart.addLineSeries(nsfSeriesOptions)
            break
          case 'diff':
            this.series.diff = chart.addBaselineSeries(diffSeriesOptions)
            break
          case 'volume':
            this.series.volume = chart.addHistogramSeries(volumeSeriesOptions)
            break
          case 'adwin':
            this.series.adwin = chart.addLineSeries(adwinSeriesOptions)
            this.series.adwin.createPriceLine(priceLine(0, 1))
            this.series.adwin.createPriceLine(priceLine(100, 2))
            this.series.adwin.createPriceLine(priceLine(-100, 2))
            break

          default:
            break
        }
      })
      if (this.series.diff) this.series.diff.createPriceLine(priceLine(0, 1))

      this.charts.forEach((chart, id) => {
        if (chart) this.bindSync(chart, id)
      })
    },
    computeData(raw) {
      const {
        t = [], o = [], h = [], l = [], c = [], v = [],
      } = raw

      const currentData = {}

      // Compute candle data if enabled
      if (this.CHART_NAMES.includes('candle')) {
        const candleData = []
        t.forEach((time, idx) => {
          time += timezoneOffset
          candleData.push({
            time,
            open: o[idx],
            low: l[idx],
            high: h[idx],
            close: c[idx],
          })
          timeIndexes[time] = idx
        })
        currentData.candle = candleData
      }

      // Compute money flow data if enabled
      if (this.CHART_NAMES.includes('money') || this.CHART_NAMES.includes('diff')) {
        const result = runProgram(moneyFlowProgram, raw)

        if (this.CHART_NAMES.includes('money')) {
          const nbfData = []
          const nsfData = []
          t.forEach((time, idx) => {
            time += timezoneOffset
            nbfData.push(point(time, result?.nbf[idx]))
            nsfData.push(point(time, result?.nsf[idx]))
          })
          currentData.nbf = nbfData
          currentData.nsf = nsfData
        }

        if (this.CHART_NAMES.includes('diff')) {
          const diffData = []
          t.forEach((time, idx) => {
            time += timezoneOffset
            diffData.push(point(time, result?.diff[idx]))
          })
          currentData.diff = diffData
        }
      }

      // Compute volume data if enabled
      if (this.CHART_NAMES.includes('volume')) {
        const volumeData = []
        t.forEach((time, idx) => {
          time += timezoneOffset
          const color = c[idx] >= o[idx] ? volumeSeriesOptions.upColor : volumeSeriesOptions.downColor
          volumeData.push({ time, value: v[idx], color })
        })
        currentData.volume = volumeData
      }

      // Compute adwin data if enabled
      if (this.CHART_NAMES.includes('adwin')) {
        const adwinResult = runProgram(adwinProgram, raw)
        const adwinData = []
        t.forEach((time, idx) => {
          time += timezoneOffset
          adwinData.push(point(time, adwinResult?.adwinDoScope[idx]))
        })
        currentData.adwin = adwinData
      }

      this.currentData = currentData

      return currentData
    },
    pushData(input) {
      const dt = applyTick(this.rawData, input, this.resolution)
      if (dt) this.rawData = dt
    },
    resetZoom() {
      const length = this.rawData.t?.length
      if (length) {
        if (this.charts[0]) {
          this.charts[0].timeScale().setVisibleLogicalRange({ from: length - 50, to: length })
        }
      }
    },
  },
}
</script>

<style scoped lang="scss" src="./_chartShared.scss"></style>

<style scoped>
.container-move.down {
  margin-top: 10px;
  top: 250px;
}
</style>
